import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRetrieveReview } from "../../hooks/review/useRetrieveReview";
import {
  QAlert,
  QBackLink,
  QBodyLayout,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QDeleteConfirmationModal,
  QHeader,
  QHorizontalMetadata,
  QMenuButton,
  QMenuItem,
  QNotFound,
  QOpenPropertiesPanelButton,
  QOwnerMetadata,
  QPageLoader,
  QPerson,
  QPersonProps,
  QPropertiesPanel,
  QResourceRouteTabs,
  QRouteTabs,
  QStack,
  QText,
  QTitle,
  QTogglePropertiesPanelButton,
  useCurrentUser,
} from "@qualio/ui-components";
import { ReviewStatus, ReviewStatusEnum } from "@design-controls/types";
import { getQRI } from "@qualio/search-qri";
import { ReviewTabContext } from "./components/ReviewTabContext";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import { PermissionCheck } from "../../components/PermissionCheck/PermissionCheck";
import { useNavigate } from "react-router";
import { useSendForApproval } from "../../hooks/review/useSendForApproval";
import { useRevertToDraft } from "../../hooks/review/useRevertToDraft";
import { useDisclosure } from "@chakra-ui/hooks";
import { useDeleteReview } from "../../hooks/review/useDeleteReview";
import { useRetrieveGeneratedDocuments } from "../../hooks/review/useRetrieveGeneratedDocuments";
import { ReviewStatusTag } from "./components/ReviewStatusTag";
import { UserPermissions } from "../../domain/permissions";

const statusTable: Partial<Record<ReviewStatus, QPersonProps["status"]>> = {
  [ReviewStatus.enum.IN_PROGRESS]: "default",
  [ReviewStatus.enum.NOT_APPROVED]: "declined",
  [ReviewStatus.enum.APPROVED]: "approved",
};

export const ReviewOverview: React.FC = () => {
  const { t } = useTranslation();
  const { isLoading, data } = useRetrieveReview();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    data: reportData,
    isComplete,
    isLoading: isLoadingGeneratedDocs,
    downloadExportPacket,
  } = useRetrieveGeneratedDocuments(data?.releaseId);

  const { companyId, formatDate, userId } = useCurrentUser();
  const { id: productId, name } = useCurrentProduct();
  const navigate = useNavigate();

  const { mutate: mutateForApproval, isLoading: isSendForApprovalLoading } =
    useSendForApproval();

  const { mutate: mutateForDraft, isLoading: isRevertToDraftLoading } =
    useRevertToDraft();

  const { isLoading: isDeleting, mutate: deleteReview } = useDeleteReview();

  const onEdit = useCallback(
    () => navigate(`../change-control/${data?.id}/edit`),
    [navigate, data],
  );

  const isOwner = useMemo(
    () => userId === Number(data?.createdBy?.id),
    [userId, data],
  );

  const canApprove = useMemo(() => {
    if (data?.status !== ReviewStatus.enum.SENT_FOR_APPROVAL) {
      return false;
    }

    return !!data.approvers.find(
      (approver) =>
        Number(approver.id) === userId &&
        approver.status === ReviewStatus.enum.IN_PROGRESS,
    );
  }, [userId, data]);

  const sendForApproval = useCallback(
    () => mutateForApproval(),
    [mutateForApproval],
  );

  const onConfirmDelete = useCallback(() => {
    if (!data || !data.id) {
      return;
    }

    deleteReview(data.id);
  }, [data, deleteReview]);

  const revertToDraft = useCallback(() => mutateForDraft(), [mutateForDraft]);

  const hasOutdatedDesignElements = useMemo(() => {
    return (
      data?.qualityItems.some(
        (item) => item.latest && item.latest !== item.version,
      ) &&
      ![ReviewStatusEnum.APPROVED, ReviewStatusEnum.NOT_APPROVED].includes(
        data.status,
      )
    );
  }, [data]);

  if (isLoading) {
    return <QPageLoader />;
  }

  if (!data) {
    return <QNotFound />;
  }

  return (
    <>
      <QStack overflow="hidden">
        <QBodyLayout.Default>
          <QHeader showDivider={!data.releaseId}>
            <QBackLink href="../change-control" data-cy="back-btn">
              {t("products.backLink", { productName: name })}
            </QBackLink>
            <QTitle data-cy="review-title">
              <QText as="span" color="gray.500">
                {data.code}
              </QText>{" "}
              {data.title}
            </QTitle>
            {hasOutdatedDesignElements && (
              <QAlert
                description={
                  isOwner
                    ? t("reviews.overview.alerts.outdatedDesignElementsOwner")
                    : t(
                        "reviews.overview.alerts.outdatedDesignElementsNonOwner",
                      )
                }
                status="warning"
                data-cy="outdated-design-elements"
              />
            )}
            <QHorizontalMetadata>
              {data.status && <ReviewStatusTag status={data.status} />}
              <QDateMetadataItem
                label={t("reviews.overview.metadata.created")}
                value={new Date(data?.created ?? 0)}
              />
              <QDateMetadataItem
                label={t("reviews.overview.metadata.lastModified")}
                value={new Date(data?.updated ?? 0)}
              />
              <QOwnerMetadata value={data.createdBy?.fullName} />
            </QHorizontalMetadata>
            <QButtonGroup>
              {isComplete && (
                <QButton
                  data-cy="export-review-packet"
                  onClick={downloadExportPacket}
                  variant="outline"
                >
                  {t("reviews.overview.actions.exportReviewPacket")}
                </QButton>
              )}
              <PermissionCheck
                permissions={[UserPermissions.EDIT_CHANGE_CONTROL]}
              >
                {isOwner && data.status === ReviewStatus.enum.IN_PROGRESS && (
                  <QButton
                    leftIcon="Edit3"
                    variant="outline"
                    data-cy="edit"
                    onClick={onEdit}
                  >
                    {t("reviews.overview.actions.edit")}
                  </QButton>
                )}
                {isOwner && data.status === ReviewStatus.enum.IN_PROGRESS && (
                  <QButton
                    data-cy="send-for-approval-button"
                    onClick={sendForApproval}
                    isLoading={isSendForApprovalLoading}
                  >
                    {t("reviews.overview.actions.sendForApproval")}
                  </QButton>
                )}
                {isOwner &&
                  data.status === ReviewStatus.enum.SENT_FOR_APPROVAL && (
                    <QButton
                      data-cy="send-for-draft-button"
                      variant="outline"
                      onClick={revertToDraft}
                      isLoading={isRevertToDraftLoading}
                    >
                      {t("reviews.overview.actions.revertToDraft")}
                    </QButton>
                  )}
                {isOwner && data.status === ReviewStatus.enum.IN_PROGRESS && (
                  <QMenuButton
                    data-cy="more"
                    iconName="MoreVertical"
                    itemSize="sm"
                    variant="icon"
                  >
                    <QMenuItem
                      isDestructive
                      onClick={onOpen}
                      data-cy="delete-review"
                    >
                      {t("reviews.overview.actions.delete")}
                    </QMenuItem>
                  </QMenuButton>
                )}
              </PermissionCheck>
              <QOpenPropertiesPanelButton />
            </QButtonGroup>
            {!data.approvers.length && (
              <QAlert
                title={t("reviews.overview.alerts.missingApprovers.title")}
                description={t(
                  "reviews.overview.alerts.missingApprovers.description",
                )}
                status="warning"
              />
            )}
          </QHeader>
          <QStack data-cy="review-overview">
            <ReviewTabContext.Provider
              value={{
                data,
                canApprove,
                reportData,
                isComplete,
                isLoading: isLoadingGeneratedDocs,
                hasOutdatedDesignElements,
              }}
            >
              <QResourceRouteTabs
                qri={[
                  getQRI({
                    qriBaseUrl:
                      import.meta.env.VITE_QRI_SERVICE_PUBLIC_URL ??
                      import.meta.env.VITE_QRI_SERVICE_HOSTNAME,
                    domain: "design-controls",
                    domainType: "design-controls-changeControl",
                    companyId: companyId.toString(10),
                    id: `${productId}#${data?.id}`,
                  }),
                ]}
              >
                <QRouteTabs.Item route="details" data-cy="review-details-tab">
                  {t("reviews.overview.tabs.details")}
                </QRouteTabs.Item>
                {isComplete && (
                  <QRouteTabs.Item
                    route="traceability"
                    data-cy="review-traceability-tab"
                  >
                    {t("reviews.overview.tabs.traceability")}
                  </QRouteTabs.Item>
                )}
              </QResourceRouteTabs>
            </ReviewTabContext.Provider>
          </QStack>
          <QPropertiesPanel openOnMount>
            <QStack spacing="8px" w="300px">
              <QStack direction="row" justifyContent="space-between">
                <QTitle>{t("reviews.overview.properties.title")}</QTitle>
                <QTogglePropertiesPanelButton />
              </QStack>
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("reviews.overview.properties.approvers")}
                </QText>
                <QStack>
                  {data.approvers.map((approver) => (
                    <QStack>
                      <QPerson
                        key={approver.id}
                        status={
                          statusTable[approver.status as ReviewStatus] ??
                          "default"
                        }
                        actionTimestamp={
                          approver.modified
                            ? formatDate(new Date(approver.modified))
                            : undefined
                        }
                        fullName={approver.fullName ?? ""}
                      />
                      {approver.note && (
                        <QText as="i" fontSize="xs" mt={1} color="gray.700">
                          {approver.note}
                        </QText>
                      )}
                    </QStack>
                  ))}
                </QStack>
              </QStack>
            </QStack>
          </QPropertiesPanel>
        </QBodyLayout.Default>
      </QStack>
      {isOpen && (
        <QDeleteConfirmationModal
          onConfirm={onConfirmDelete}
          onClose={onClose}
          title={t("reviews.delete.modalTitle")}
          inProgress={isDeleting}
          message={
            <QText fontSize="sm">
              {t("reviews.delete.confirmMessage", { reviewTitle: data?.title })}
            </QText>
          }
        />
      )}
    </>
  );
};
